// ===========================================
// Record the notes of a Meet & Greet (#255, enquiries Phase 4)
// ===========================================
// Used by the teacher's own page (`mode="teacher"`) and by the office on a teacher's behalf
// (`mode="admin"`, needs meet_and_greet.write). Attendance first: a no-show hides the attended-only
// fields and the outcome. Notes are append-only: a correction is a NEW note.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRecordAdminNotes, useRecordTeacherNotes } from '../../hooks/useMeetAndGreetNotes';
import type { NoteAttendance, NoteEngagement, NoteOutcome } from '../../api/meetAndGreetNotes.api';
import {
  ATTENDANCE_LABELS,
  EMPTY_NOTES_FORM,
  ENGAGEMENT_LABELS,
  NOTES_LIMITS,
  OUTCOME_LABELS,
  buildNotesBody,
  validateNotesForm,
  type NotesFormState,
} from '../../utils/meetAndGreetNotes';

interface RecordNotesDialogProps {
  open: boolean;
  onClose: () => void;
  meetAndGreetId: string;
  mode: 'teacher' | 'admin';
  childName: string;
}

export default function RecordNotesDialog({ open, onClose, meetAndGreetId, mode, childName }: RecordNotesDialogProps) {
  const teacherMutation = useRecordTeacherNotes();
  const adminMutation = useRecordAdminNotes();
  const mutation = mode === 'teacher' ? teacherMutation : adminMutation;

  const [form, setForm] = useState<NotesFormState>(EMPTY_NOTES_FORM);
  const [showErrors, setShowErrors] = useState(false);

  // A fresh form every time it opens (a second note is a new note, never an edit of the last).
  useEffect(() => {
    if (open) {
      setForm(EMPTY_NOTES_FORM);
      setShowErrors(false);
    }
  }, [open]);

  const errors = validateNotesForm(form);
  const set = <K extends keyof NotesFormState>(key: K, value: NotesFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const err = (key: keyof NotesFormState) => (showErrors ? errors[key] : undefined);

  const attended = form.attendance === 'ATTENDED';
  const noShow = form.attendance === 'NO_SHOW';

  const submit = () => {
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }
    mutation.mutate(
      { id: meetAndGreetId, input: buildNotesBody(form) },
      { onSuccess: () => onClose() },
    );
  };

  const textField = (
    key: 'priorExperience' | 'observations' | 'recommendedProgram' | 'recommendedClass' | 'notesForTeam',
    label: string,
    options: { multiline?: boolean; required?: boolean; helper?: string } = {},
  ) => (
    <TextField
      label={label}
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      multiline={options.multiline}
      minRows={options.multiline ? 3 : undefined}
      required={options.required}
      fullWidth
      error={Boolean(err(key)) || form[key].trim().length > NOTES_LIMITS[key]}
      helperText={err(key) ?? options.helper}
    />
  );

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record notes: {childName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Notes cannot be edited once saved. To correct something, record a new note and the office will read the
            latest one first.
          </Alert>

          <FormControl error={Boolean(err('attendance'))}>
            <FormLabel id="mng-attendance-label">Did the family come?</FormLabel>
            <RadioGroup
              aria-labelledby="mng-attendance-label"
              value={form.attendance}
              onChange={(e) => set('attendance', e.target.value as NoteAttendance)}
            >
              {(Object.keys(ATTENDANCE_LABELS) as NoteAttendance[]).map((key) => (
                <FormControlLabel key={key} value={key} control={<Radio />} label={ATTENDANCE_LABELS[key]} />
              ))}
            </RadioGroup>
            {err('attendance') && <FormHelperText>{err('attendance')}</FormHelperText>}
          </FormControl>

          {noShow && (
            <>
              {textField('observations', 'What happened (optional)', { multiline: true })}
              {textField('notesForTeam', 'Notes for the team (optional)', { multiline: true })}
            </>
          )}

          {attended && (
            <>
              <TextField
                select
                label="How engaged was the child? (optional)"
                value={form.engagement}
                onChange={(e) => set('engagement', e.target.value as NoteEngagement | '')}
                fullWidth
              >
                <MenuItem value="">Not recorded</MenuItem>
                {(Object.keys(ENGAGEMENT_LABELS) as NoteEngagement[]).map((key) => (
                  <MenuItem key={key} value={key}>
                    {ENGAGEMENT_LABELS[key]}
                  </MenuItem>
                ))}
              </TextField>
              {textField('priorExperience', 'Prior experience (optional)')}
              {textField('observations', 'Observations', { multiline: true, required: true })}
              {textField('recommendedProgram', 'Recommended program (optional)')}
              {textField('recommendedClass', 'Recommended class (optional)')}
              {textField('notesForTeam', 'Notes for the team (optional)', { multiline: true })}
              <TextField
                select
                required
                label="Outcome"
                value={form.outcome}
                onChange={(e) => set('outcome', e.target.value as NoteOutcome | '')}
                fullWidth
                error={Boolean(err('outcome'))}
                helperText={err('outcome')}
              >
                {(Object.keys(OUTCOME_LABELS) as NoteOutcome[]).map((key) => (
                  <MenuItem key={key} value={key}>
                    {OUTCOME_LABELS[key]}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {!form.attendance && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Choose whether the family came to see the rest of the form.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={mutation.isPending || !form.attendance}>
          {mutation.isPending ? 'Saving...' : 'Save notes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
